import { useState } from 'react';
import db from '@/db';
import { Bell, User, Store, Phone, RefreshCw, CheckCircle2, Package, Bike, ChefHat, PartyPopper } from 'lucide-react';
import { formatBrPhone } from '@/lib/notifications';
import { useVisiblePolling } from '@/hooks/useVisiblePolling';

// Metadata visual por tipo de status
const STATUS_META = {
  confirmed: { label: 'Pedido Confirmado', icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  preparing: { label: 'Na Cozinha', icon: ChefHat, color: 'bg-amber-100 text-amber-700 border-amber-200' },
  ready: { label: 'Pronto', icon: Package, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  delivering: { label: 'Saiu p/ Entrega', icon: Bike, color: 'bg-purple-100 text-purple-700 border-purple-200' },
  delivered: { label: 'Entregue', icon: PartyPopper, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  restaurant_new_order: { label: '🔔 Novo Pedido p/ Restaurante', icon: Store, color: 'bg-rose-100 text-rose-700 border-rose-200' },
};

function relativeTime(iso) {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'agora há pouco';
  if (min < 60) return `${min} min atrás`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h atrás`;
  const d = Math.floor(h / 24);
  return `${d}d atrás`;
}

export default function NotificationLog({ limit = 15 }) {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    // Fase 27 — limit no Dexie (não buscar tabela inteira)
    const recent = await db.notifications.orderBy('sentAt').reverse().limit(limit * 2).toArray();
    setNotifs(recent.slice(0, limit));
    setLoading(false);
  };

  useVisiblePolling(load, 10000);

  const total = notifs.length;

  return (
    <section data-testid="notification-log" className="bg-white border border-slate-200 rounded-2xl p-5">
      <header className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-900 to-slate-700 text-white flex items-center justify-center">
            <Bell size={14} strokeWidth={2} />
          </div>
          <div>
            <p className="font-heading font-bold text-slate-900 text-sm">Log de Notificações</p>
            <p className="text-[11px] text-slate-500">
              Últimas {limit} mensagens do robô WhatsApp (mock) — cliente e restaurante
            </p>
          </div>
        </div>
        <button
          data-testid="notif-log-refresh"
          onClick={load}
          className="flex items-center gap-1.5 px-3 h-8 rounded-lg border border-slate-200 bg-white text-xs font-bold text-slate-600 hover:bg-slate-50"
        >
          <RefreshCw size={12} /> Atualizar
        </button>
      </header>

      {loading ? (
        <p className="text-center text-sm text-slate-400 py-6">Carregando...</p>
      ) : total === 0 ? (
        <div data-testid="notif-log-empty" className="text-center py-8">
          <Bell size={28} className="mx-auto text-slate-300 mb-2" strokeWidth={1.5} />
          <p className="text-sm font-semibold text-slate-500">Nenhuma notificação ainda</p>
          <p className="text-[11px] text-slate-400 mt-0.5">
            O robô registra mensagens aqui quando um pedido é confirmado ou muda de status.
          </p>
        </div>
      ) : (
        <ul data-testid="notif-log-list" className="space-y-2">
          {notifs.map((n) => {
            const meta = STATUS_META[n.status] || {
              label: n.status || 'Evento',
              icon: Bell,
              color: 'bg-slate-100 text-slate-700 border-slate-200',
            };
            const Icon = meta.icon;
            const isRestaurant = n.status === 'restaurant_new_order';
            const recipient = isRestaurant ? 'Restaurante' : 'Cliente';
            const RecipIcon = isRestaurant ? Store : User;
            // Preview: primeira linha útil da mensagem
            const preview = (n.message || '')
              .split('\n')
              .map((l) => l.replace(/\*/g, '').trim())
              .filter(Boolean)[0] || '';

            return (
              <li
                key={n.id}
                data-testid={`notif-item-${n.id}`}
                className="flex items-start gap-3 bg-slate-50/60 border border-slate-100 rounded-xl px-3 py-2.5 hover:bg-slate-50 transition-colors"
              >
                <div className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${meta.color}`}>
                  <Icon size={14} strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-slate-800">{meta.label}</span>
                    {n.orderId && (
                      <span className="font-mono text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                        #{n.orderId}
                      </span>
                    )}
                    <span className="text-[10px] text-slate-400">{relativeTime(n.sentAt)}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 truncate mt-0.5">
                    <RecipIcon size={10} className="inline mr-1 -mt-0.5" strokeWidth={2} />
                    <span className="font-semibold">{recipient}</span>
                    {n.to && (
                      <>
                        <span className="mx-1 text-slate-300">·</span>
                        <Phone size={9} className="inline mr-0.5 -mt-0.5" />
                        <span className="font-mono">{formatBrPhone(n.to) || n.to}</span>
                      </>
                    )}
                  </p>
                  <p className="text-[11px] text-slate-600 truncate mt-0.5 italic">{preview}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
